/**
 * DeleteStageDialog — deleting a stage on Admin > Stage Pipeline (R18–R20,
 * R31). Patients may sit in the stage, so the admin picks which other live
 * stage its plans move to; the move is quiet (no task, text or email) and the
 * stage is soft-deleted, restorable from Show deleted. Account appointment
 * settings that send plans to the stage's type are warnings only.
 *
 * Laid out like ConfirmDialog (title in the header, Cancel + red confirm),
 * but with a target picker in the body, which ConfirmDialog's <p> can't hold.
 */
import { useState } from 'react';
import { AlertCircle, AlertTriangle } from 'lucide-react';
import { DialogField } from '@/components/admin/DialogChrome';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { getErrorMessage } from '@/services/apiClient';
import {
  StageInUseError,
  deleteStage,
  type PipelineStage,
  type StageWarning,
} from '@/services/stagePipelineAdmin.service';
import { toast } from '@/store/toastStore';
import { stageDeleteBlock } from './stagePipelineModel';

export interface DeleteStageDialogProps {
  stage: PipelineStage;
  /** The account's other live stages, in pipeline order. */
  targets: PipelineStage[];
  warnings: StageWarning[];
  onClose: () => void;
  /** The stage is deleted and `moved` plans now sit in `target`. */
  onDeleted: (target: PipelineStage, moved: number) => void;
  /** The server refused: automations still point at the stage. */
  onBlocked: () => void;
}

export function DeleteStageDialog({ stage, targets, warnings, onClose, onDeleted, onBlocked }: DeleteStageDialogProps) {
  const [targetId, setTargetId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const target = targets.find((t) => t.id === targetId) ?? null;

  async function handleDelete() {
    if (!target || saving) return;
    setSaving(true);
    try {
      const moved = await deleteStage(stage.id, target.id);
      onDeleted(target, moved);
    } catch (err) {
      setSaving(false);
      if (err instanceof StageInUseError) {
        toast.error(stageDeleteBlock(err.blockers, err.pendingPlanTasks) ?? err.message);
        onBlocked();
        return;
      }
      toast.error(getErrorMessage(err, 'The stage could not be deleted.'));
    }
  }

  return (
    <Modal
      isOpen
      onClose={saving ? () => {} : onClose}
      size="sm"
      hideCloseButton={saving}
      title={
        <div className="flex items-center gap-2">
          <AlertCircle className="h-5 w-5 flex-shrink-0 text-red-500" aria-hidden="true" />
          <h2 className="text-base font-semibold text-slate-900">{`Delete stage "${stage.name}"?`}</h2>
        </div>
      }
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="danger" onClick={() => void handleDelete()} loading={saving} disabled={!target}>
            Delete stage
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-slate-600">
          Every plan in {stage.name} moves to the stage you pick, with its plan status cleared. No task, text or email goes out.
          You can restore {stage.name} from Show deleted.
        </p>
        <DialogField label="Move its plans to" htmlFor="delete-stage-target" required>
          <Select<number>
            id="delete-stage-target"
            placeholder="Pick a stage…"
            options={targets.map((t) => ({ value: t.id, label: t.name }))}
            value={targetId}
            onChange={setTargetId}
          />
        </DialogField>
        {warnings.length > 0 && (
          <div role="note" className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
            <p className="flex items-center gap-1.5 font-medium">
              <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
              The account's appointment settings send plans to this stage:
            </p>
            <ul className="mt-1.5 list-disc space-y-0.5 pl-6">
              {warnings.map((w) => (
                <li key={w.setting}>{w.label}</li>
              ))}
            </ul>
            <p className="mt-1.5 text-xs text-amber-700">These settings don't block the delete.</p>
          </div>
        )}
      </div>
    </Modal>
  );
}
